import type { Updater } from '@tanstack/react-table'
import { type RefObject, useRef } from 'react'
import {
  createStore,
  type StateCreator,
  type StoreApi,
  useStore,
} from 'zustand'
import { persist } from 'zustand/middleware'

import { createLocalAndUrlStorage } from './localAndUrlStorage'

export interface Pagination {
  pageIndex: number
  pageSize: number
}

interface PaginationStoreActions {
  setPagination: (updater: Updater<Pagination>) => void
  setPage: (pageIndex: number) => void
  setPageSize: (pageSize: number) => void
  clearPagination: () => void
}

export interface PaginationStore {
  pageIndex: number
  pageSize: number
  actions: PaginationStoreActions
}

export const createPaginationStore = (
  contextId?: string,
  initialPageSize = 100,
) => {
  const stateCreator: StateCreator<PaginationStore> = (set, get, store) => ({
    contextId,
    pageIndex: 0,
    pageSize: initialPageSize,

    actions: {
      setPagination: (updater) => {
        const { pageIndex, pageSize } = get()
        const newPagination: Pagination =
          typeof updater === 'function'
            ? updater({ pageIndex, pageSize })
            : updater

        set(newPagination)
      },
      setPage: (pageIndex) => set({ pageIndex }),
      setPageSize: (pageSize) => set({ pageSize }),
      clearPagination: () => set(store.getInitialState()),
    },
  })

  if (contextId) {
    const isValidParam = (param: string) => ['perPage', 'page'].includes(param)
    const PARAM_TO_PROP: Record<string, string> = {
      page: 'pageIndex',
      perPage: 'pageSize',
    }

    return createStore<PaginationStore>()(
      persist(stateCreator, {
        name: `militao-${contextId}-pagination`,
        partialize: (state) => ({
          pageIndex: state.pageIndex,
          pageSize: state.pageSize,
        }),
        storage: createLocalAndUrlStorage<Pagination>({
          getParams: (searchParams) =>
            Array.from(searchParams.keys()).filter(isValidParam),

          fromParams: (searchParams) => {
            return Array.from(searchParams.entries())
              .filter(([key, value]) => isValidParam(key) && Number(value))
              .map(([key, value]) => [
                key,
                key === 'page' ? Number(value) - 1 : Number(value),
              ])
              .reduce(
                (props, [key, value]) => ({
                  ...props,
                  [PARAM_TO_PROP[key]]: value,
                }),
                {},
              )
          },

          toParams: (state: Pagination): Record<string, string> => {
            return {
              page: String(state.pageIndex + 1),
              perPage: String(state.pageSize),
            }
          },
        }),
      }),
    )
  }

  return createStore<PaginationStore>(stateCreator)
}

const usePaginationStore = <T>(
  storeRef: RefObject<StoreApi<PaginationStore> | null>,
  selector: (state: PaginationStore) => T,
  contextId?: string,
  initialPageSize?: number,
): T => {
  if (!storeRef.current) {
    storeRef.current = createPaginationStore(contextId, initialPageSize)
  }
  return useStore(storeRef.current, selector)
}

export const usePagination = (contextId?: string, initialPageSize?: number) => {
  const storeRef = useRef(null)
  const pageIndex = usePaginationStore(
    storeRef,
    (state: PaginationStore) => state.pageIndex,
    contextId,
    initialPageSize,
  )
  const pageSize = usePaginationStore(
    storeRef,
    (state: PaginationStore) => state.pageSize,
    contextId,
    initialPageSize,
  )

  const actions = usePaginationStore(
    storeRef,
    (state: PaginationStore) => state.actions,
    contextId,
    initialPageSize,
  )

  return { pageIndex, pageSize, actions }
}
